import type { PaginatedResponse } from "@/pages/_index/+handler";
import type { CastCollection, CastItem } from "@/types/aggregate-items";
import {
  logError,
  logInfo,
  logPerformance,
  logUserInteraction,
} from "@/utils/logger";
import transition from "@/utils/transition";

export interface Input {
  results: PaginatedResponse;
  influencers?: string[];
}

export interface State {
  items: (CastItem | CastCollection)[];
  allItemsLoaded: boolean;
  latestIndex: number;
  loading: boolean;
  isLoadingFromStorage: boolean;
  refreshKey?: number;
}

export default class CastDiscoverGrid extends Marko.Component<Input, State> {
  page: number = 1;
  limit: number = 5;
  totalItems: number = 0;
  totalPages: number = 0;
  csrfToken: string = "";
  influencers: string[] = [];

  onCreate(input: Input, out: Marko.Out) {
    this.state = {
      allItemsLoaded: input.results.totalItems <= input.results.items.length,
      items: input.results.items,
      latestIndex: 0,
      loading: false,
      isLoadingFromStorage: false,
      refreshKey: 0,
    };

    this.page = input.results.page;
    this.limit = input.results.limit;
    this.totalItems = input.results.totalItems;
    this.totalPages = input.results.totalPages;
    this.csrfToken = out.global.csrfToken as string;
    this.influencers = input.influencers || [];
  }

  loadState() {
    const saved = sessionStorage.getItem("discover-grid");
    if (saved) {
      const { items, page, limit, totalPages, influencers } = JSON.parse(saved);
      this.page = page;
      this.limit = limit;
      this.state.isLoadingFromStorage = true;
      this.state.items = items;
      this.state.allItemsLoaded = this.page >= totalPages;
      this.state.refreshKey = 1;
      if (influencers) {
        this.influencers = influencers;
      }
    }
  }

  saveState() {
    // Save to session storage
    sessionStorage.setItem(
      "discover-grid",
      JSON.stringify({
        items: this.state.items,
        page: this.page,
        limit: this.limit,
        totalItems: this.totalItems,
        totalPages: this.totalPages,
        influencers: this.influencers,
      }),
    );
  }

  async fetchMoreItems() {
    const startTime = Date.now();
    this.state.latestIndex = this.state.items.length;
    this.state.loading = true;
    this.state.isLoadingFromStorage = false;

    // Log that user is loading more items
    logUserInteraction("load_more", "discover_grid", {
      currentPage: this.page,
      currentItemCount: this.state.items.length,
    });

    // Build query parameters for influencers
    const queryParams = new URLSearchParams();
    if (this.influencers.length > 0) {
      queryParams.set("influencers", this.influencers.join(","));
    }
    const queryString = queryParams.toString();
    const url = `/api/discover-items/${this.page + 1}/${this.limit}${queryString ? `?${queryString}` : ""}`;

    const result = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken,
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        if (response.status === 403) {
          throw new Error("Invalid request");
        }
        return response.json();
      })
      .catch((error) => {
        console.error("fetch error", error);

        // Log the fetch error
        logError(error, {
          context: "discover_grid_fetch",
          page: this.page + 1,
          limit: this.limit,
          currentItemCount: this.state.items.length,
        });

        return undefined;
      });

    this.state.loading = false;

    // Log performance metrics
    const fetchDuration = Date.now() - startTime;
    logPerformance("discover_grid_fetch_duration", fetchDuration);

    if (result) {
      this.page = result.page;
      await transition(() => {
        this.state.items = this.state.items.concat(result.items);
        this.state.allItemsLoaded =
          result.totalItems <= this.state.items.length;
      });
      this.saveState();

      // Log successful load
      logInfo("discover_grid_items_loaded", {
        newItemsCount: result.items.length,
        totalItemsCount: this.state.items.length,
        page: this.page,
        allItemsLoaded: this.state.allItemsLoaded,
      });
    }
  }

  async handleLoadMore() {
    if (this.state.loading) {
      // Log attempted load more while already loading
      logUserInteraction("load_more_attempted", "discover_grid", {
        status: "already_loading",
        currentItemCount: this.state.items.length,
      });
      return;
    }

    await this.fetchMoreItems();
  }
}
